'use client'

import { useCurrentClient, useCurrentUser } from '@tebuto/functions/hooks'
import { RootState } from '@tebuto/state/store'
import { setTherapistId } from '@tebuto/state/therapist'
import { createContext, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import ClientTherapistSelection from './selections/ClientTherapistSelection'
import { activeClientTherapists } from './selections/client-portal-therapists'

export const SelectedTherapistIdContext = createContext<number | null>(null)

export function TherapistSelectionGuard({ children }: { children: React.ReactNode }) {
    const dispatch = useDispatch()
    const selectedTherapistId = useSelector((state: RootState) => state.therapistContext.id)
    const currentUser = useCurrentUser()
    const [validatedTherapistId, setValidatedTherapistId] = useState<number | null>(null)

    // Build a set of valid therapist IDs for the current user
    const validTherapistIds = useMemo(
        () => new Set(currentUser.therapists.map((t) => t.therapist.id)),
        [currentUser.therapists]
    )

    useEffect(() => {
        // Check if the currently selected therapist is valid for this user
        const isValidTherapist = selectedTherapistId && validTherapistIds.has(selectedTherapistId)

        if (isValidTherapist) {
            // Selected therapist is valid, proceed
            setValidatedTherapistId(selectedTherapistId)
            return
        }

        // The selected therapist is NOT valid for this user (could be from a different user session)
        // This is a security fix: always reset to a valid therapist for the current user
        if (currentUser.therapists.length >= 1) {
            const correctTherapistId = currentUser.therapists[0].therapist.id
            // Reset Redux state to a valid therapist
            dispatch(setTherapistId(correctTherapistId))
            // Also update local state immediately to prevent race condition
            setValidatedTherapistId(correctTherapistId)
        }
        // If user has no therapists, they'll stay in loading state (shouldn't happen in normal use)
    }, [currentUser, selectedTherapistId, validTherapistIds, dispatch])

    // CRITICAL: Only render children when we have a validated therapist ID
    // This prevents the race condition where children render with an invalid therapist ID
    if (!validatedTherapistId || !validTherapistIds.has(validatedTherapistId)) {
        return <></>
    }
    return (
        <SelectedTherapistIdContext.Provider value={validatedTherapistId}>
            {children}
        </SelectedTherapistIdContext.Provider>
    )
}

export function TherapistClientSelectionGuard({ children }: { children: React.ReactNode }) {
    const dispatch = useDispatch()
    const selectedTherapistId = useSelector((state: RootState) => state.therapistContext.id)
    const currentClient = useCurrentClient()
    const [validatedTherapistId, setValidatedTherapistId] = useState<number | null>(null)

    // Build a set of valid therapist IDs for the current client
    const activeTherapists = useMemo(() => activeClientTherapists(currentClient.therapists), [currentClient.therapists])
    const validTherapistIds = useMemo(() => new Set(activeTherapists.map((t) => t.id)), [activeTherapists])

    useEffect(() => {
        // Check if the currently selected therapist is valid for this client
        const isValidTherapist = selectedTherapistId && validTherapistIds.has(selectedTherapistId)

        if (isValidTherapist) {
            setValidatedTherapistId(selectedTherapistId)
            return
        }

        // The selected therapist is NOT valid for this client - reset to a valid one
        if (activeTherapists.length === 1) {
            const correctTherapistId = activeTherapists[0].id
            dispatch(setTherapistId(correctTherapistId))
            setValidatedTherapistId(correctTherapistId)
        } else {
            setValidatedTherapistId(null)
        }
    }, [activeTherapists, selectedTherapistId, validTherapistIds, dispatch])

    // CRITICAL: Only render children when we have a validated therapist ID
    if (!validatedTherapistId || !validTherapistIds.has(validatedTherapistId)) {
        // Nothing can be auto-selected: let the selection screen explain why
        if (activeTherapists.length !== 1) {
            return <ClientTherapistSelection />
        }
        // Otherwise, wait for validation to complete
        return <></>
    }

    return (
        <SelectedTherapistIdContext.Provider value={validatedTherapistId}>
            {children}
        </SelectedTherapistIdContext.Provider>
    )
}
